import { Component } from '@theme/component';

/**
 * Announcement banner custom element that slides between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * Timeout for clearing slide exit state after transition.
   * @type {number|undefined}
   */
  #transitionTimeout = undefined;

  /** @type {number} */
  #transitionDuration = 500;

  disconnectedCallback() {
    clearTimeout(this.#transitionTimeout);
    this.suspend();
    super.disconnectedCallback();
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slides = this.refs.slides ?? [];
    const length = slides.length;

    if (length === 0) return;

    const previousIndex = ((this.#current % length) + length) % length;
    let nextIndex = current % length;

    if (nextIndex < 0) {
      nextIndex += length;
    }

    this.#current = current;

    if (length === 1 || previousIndex === nextIndex) {
      slides.forEach((slide, index) => {
        slide.classList.remove('is-exiting');
        slide.setAttribute('aria-hidden', `${index !== nextIndex}`);
      });
      return;
    }

    const outgoing = slides[previousIndex];
    const incoming = slides[nextIndex];

    slides.forEach((slide, index) => {
      if (index !== previousIndex && index !== nextIndex) {
        slide.classList.remove('is-exiting');
        slide.setAttribute('aria-hidden', 'true');
      }
    });

    incoming.setAttribute('aria-hidden', 'false');
    outgoing.classList.add('is-exiting');
    outgoing.setAttribute('aria-hidden', 'true');

    clearTimeout(this.#transitionTimeout);
    this.#transitionTimeout = window.setTimeout(() => {
      outgoing.classList.remove('is-exiting');
    }, this.#transitionDuration);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}

/**
 * Opens the country selector modal from announcement bar language selector.
 *
 * @param {Event} event
 */
function handleAnnouncementBarLanguageSelectorClick(event) {
  event.preventDefault();
  window.PubSub?.publish('open-country-selector');
}

function initAnnouncementBarLanguageSelector() {
  document.querySelectorAll('.announcement-bar__language-selector-button').forEach((button) => {
    button.addEventListener('click', handleAnnouncementBarLanguageSelectorClick);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAnnouncementBarLanguageSelector);
} else {
  initAnnouncementBarLanguageSelector();
}
